import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Button,
    Typography,
    Form,
    Input,
    Grid,
    List,
    App as AntdApp,
    theme,
    Skeleton,
    Flex,
    message
} from 'antd';
import ResponsiveModal from '../Shared/ResponsiveModal/ResponsiveModal';
import {
    PlusOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { clearAuthData } from '../../Config/ApiConfig';
import { fbLogout } from '../../Utils/facebook';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import {
    requestDataExport,
    deleteAccount
} from './SettingsServices';
import { Resource } from '../../Constants/socket';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { useNavigate } from 'react-router-dom';
import { useExportImportProgress } from '../../Contexts/ExportImportProgressContext';

const { useBreakpoint } = Grid;
const { Title, Text, Link } = Typography;

const Settings = () => {
    const [form] = Form.useForm();
    const { token } = theme.useToken();
    const { notification } = AntdApp.useApp();
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { handleError } = useErrorHandler();
    const { loading, setLoading } = useGlobal();
    const navigate = useNavigate();
    const [isExportVisible, setIsExportVisible] = useState(false);
    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const screens = useBreakpoint() || {};
    const { addTask } = useExportImportProgress();

    // Reset form when modal closes
    useEffect(() => {
        if (!isDeleteVisible) {
            form.resetFields();
        }
    }, [isDeleteVisible, form]);

    const handleExport = async () => {
        try {
            setExporting(true);

            // Export all available resources
            const resources = [
                Resource.AGENT,
                Resource.KNOWLEDGE_BASE,
                Resource.KNOWLEDGE_ENTRY,
                Resource.CONTACT,
                Resource.CONVERSATION,
                Resource.MESSAGE,
                Resource.TRANSACTION
            ];

            const response = await requestDataExport({
                resources,
                format: 'json'
            });

            // Add to global progress tracker
            if (response?.data?.id) {
                const resourceKeys = {
                    [Resource.AGENT]: 'agents',
                    [Resource.KNOWLEDGE_BASE]: 'knowledge_bases',
                    [Resource.KNOWLEDGE_ENTRY]: 'knowledge_entries',
                    [Resource.CONTACT]: 'contacts',
                    [Resource.CONVERSATION]: 'conversations',
                    [Resource.MESSAGE]: 'messages',
                    [Resource.TRANSACTION]: 'transactions'
                };
                const resourceNames = resources.map(r => t(resourceKeys[r] || r.toLowerCase())).join(', ');
                addTask({
                    id: response.data.id,
                    type: 'export',
                    name: `${resourceNames}.zip`,
                    status: 'processing',
                    progress: 0,
                    serverCreatedAt: response.server_datetime
                });
            }

            const text = response?.message_code ? resolveBackendMessage(t, response) : t('export_started_successfully');
            message.success(text);

            setIsExportVisible(false);
        } catch (error) {
            handleError(error);
        } finally {
            setExporting(false);
        }
    };


    const handleDelete = async () => {
        try {
            setDeletingAccount(true);
            await form.validateFields();

            // Call the deleteAccount service
            await deleteAccount();

            notification.warning({
                message: t('account_deletion_scheduled'),
                description: t('account_deletion_description')
            });

            // Redirect to login page after account deletion is scheduled
            setTimeout(async () => {
                try {
                    // Logout from Facebook if applicable
                    await fbLogout();
                    // Clear auth data
                    clearAuthData();
                    // Redirect to login
                    navigate('/login');
                } catch (error) {
                    handleError(error);
                    navigate('/login');
                }
            }, 2000);

            setIsDeleteVisible(false);
        } catch (error) {
            if (error.response) {
                handleError(error);
            } else {
                // Form validation error - handled by form UI
            }
        } finally {
            setDeletingAccount(false);
        }
    };



    const cardStyle = {
        backgroundColor: token.colorBgContainer,
        color: token.colorText,
        border: isDarkMode ? `1px solid ${token.colorBorder}` : 'none',
    };

    return (
        <div style={{ padding: 0 }}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '24px',
                marginBottom: '24px'
            }}>
                {/* Data Portability Card */}
                <Card
                    bordered={false}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        ...cardStyle
                    }}
                    title={<Title style={{ margin: 0, fontSize: '18px' }} level={4}>{t('data_portability')}</Title>}
                >
                    {loading ? (
                        <Skeleton active paragraph={{ rows: 3 }} />
                    ) : (
                        <Flex vertical justify="space-between" style={{ height: '100%', flex: 1 }}>
                            <Text type="secondary" style={{ marginBottom: 24, fontSize: '14px', lineHeight: '1.6' }}>
                                {t('export_includes_description')}
                            </Text>
                            <Button
                                color='primary'
                                variant='outlined'
                                onClick={() => setIsExportVisible(true)}
                            >
                                {t('request_data_export')}
                            </Button>
                        </Flex>
                    )}
                </Card>

                {/* Account Deletion Card */}
                <Card
                    bordered={false}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        ...cardStyle
                    }}
                    title={<Title style={{ margin: 0, fontSize: '18px' }} level={4}>{t('account_deletion')}</Title>}
                >
                    {loading ? (
                        <Skeleton active paragraph={{ rows: 3 }} />
                    ) : (
                        <Flex vertical justify="space-between" style={{ height: '100%', flex: 1 }}>
                            <Text type="secondary" style={{ marginBottom: 24, fontSize: '14px', lineHeight: '1.6' }}>
                                {t('account_deletion_warning')}
                            </Text>
                            <Button
                                danger
                                onClick={() => setIsDeleteVisible(true)}
                            >
                                {t('initiate_account_deletion')}
                            </Button>
                        </Flex>
                    )}
                </Card>

                {/* Legal Documents Card */}
                <Card
                    bordered={false}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        ...cardStyle
                    }}
                    title={<Title style={{ margin: 0, fontSize: '18px' }} level={4}>{t('legal_documents')}</Title>}
                >
                    {loading ? (
                        <Skeleton active paragraph={{ rows: 3 }} />
                    ) : (
                        <Flex vertical style={{ height: '100%', flex: 1 }}>
                            <List
                                itemLayout="horizontal"
                                split={false}
                                dataSource={[
                                    {
                                        title: t('privacy_policy'),
                                        link: '../privacy.html'
                                    },
                                    {
                                        title: t('terms_of_service'),
                                        link: '../terms.html'
                                    }
                                ]}
                                renderItem={(item) => (
                                    <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                        <Link href={item.link} target="_blank" style={{ fontSize: '14px' }}>
                                            {item.title}
                                        </Link>
                                    </List.Item>
                                )}
                            />
                        </Flex>
                    )}
                </Card>
            </div>

            {/* Delete Account Modal */}
            <ResponsiveModal
                title={t('confirm_account_deletion')}
                open={isDeleteVisible}
                onOk={handleDelete}
                onCancel={() => {
                    setIsDeleteVisible(false);
                    form.resetFields();
                }}
                okButtonProps={{ danger: true }}
                confirmLoading={deletingAccount}
                desktopWidth={520}
                okText={t('confirm')}
            >
                <Form form={form} layout="vertical">
                    <p>{t('permanent_data_erasure_warning')}</p>
                    <Form.Item
                        name="confirmation"
                        label={t('type_delete_my_account')}
                        rules={[
                            {
                                required: true,
                                message: t('please_type_confirmation_phrase'),
                                validator: (_, value) =>
                                    value === t('delete_account_confirmation_phrase') ?
                                        Promise.resolve() : Promise.reject()
                            }
                        ]}
                    >
                        <Input placeholder={t('enter_confirmation_phrase')} />
                    </Form.Item>
                </Form>
            </ResponsiveModal>

            {/* Export Request Modal */}
            <ResponsiveModal
                title={t('data_export_request')}
                open={isExportVisible}
                onOk={handleExport}
                onCancel={() => setIsExportVisible(false)}
                confirmLoading={exporting}
                desktopWidth={520}
                okText={t('proceed')}
            >
                <p>{t('export_includes_description')}</p>
            </ResponsiveModal>


        </div>
    );
};

export default Settings;
